import React, { useState, useEffect, useRef } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import { getLeaseRecordsApiErrorMessage } from '../../LeaseRecordsApiError';
import { ILeaseMaster } from 'src/models/dlcLease/ILeaseMaster';
import { ILeaseInvoice } from 'src/models/dlcLease/ILeaseInvoice';
import { ICheckDetails, IProcessCheckInput } from 'src/models/dlcLease/IDirectLessorCheck';
import { ILeaseCheck } from 'src/models/dlcLease/ILeaseCheck';
import CheckDetailsModal from 'src/components/invoicing/components/CheckDetailsModal';
import { FileText, Loader2, X, AlertTriangle } from 'src/components/common/icons';
import { DateField, Field, SelectField } from '../Master';
import useCheckNumberValidation from './useCheckNumberValidation';

interface ICreateLessorDirectCheckModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: () => void;
    leaseMaster: ILeaseMaster;
    invoice: ILeaseInvoice | null;
}

interface ICompany {
    id: number;
    name: string;
}

const CreateLessorDirectCheckModal: React.FC<ICreateLessorDirectCheckModalProps> = ({ isOpen, onClose, onSuccess, leaseMaster, invoice }) => {
    const { post, get, response } = useFetch();
    const isMounted = useRef(false);

    const [loading, setLoading] = useState<boolean>(false);
    const [formData, setFormData] = useState<ICheckDetails | null>(null);
    const [errorMsg, setErrorMsg] = useState<string | null>(null);
    const [companies, setCompanies] = useState<ICompany[]>([]);

    const { isValidating, validationError, isCheckAvailable } = useCheckNumberValidation({
        checkNo: formData?.checkNo,
        compId: formData?.compId,
    });

    const [createdCheck, setCreatedCheck] = useState<ILeaseCheck | null>(null);

    // --- 0. MOUNT TRACKING ---
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    // --- 1. POPULATE: Fetch Data on Open ---
    useEffect(() => {
        if (isOpen && invoice && leaseMaster) {
            // FIX: Wrap async call to prevent "destroy is not a function" error
            const init = async () => {
                await populateCheckData();
            };
            init();
        } else {
            setFormData(null);
            setErrorMsg(null);
            setCreatedCheck(null);
            setCompanies([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, invoice, leaseMaster]);

    // --- 2. CALCULATE: Auto-sum Total ---
    useEffect(() => {
        if (formData) {
            const calculatedTotal =
                (formData.bonus || 0) + (formData.rental || 0) + (formData.other || 0) + (formData.bankFees || 0) + (formData.fwdBankFees || 0);

            if (formData.total !== calculatedTotal) {
                setFormData((prev) => (prev ? { ...prev, total: calculatedTotal } : null));
            }
        }
        // Recalculate only when an amount component changes; including the complete
        // form object would rerun this effect after it writes the calculated total.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData?.bonus, formData?.rental, formData?.other, formData?.bankFees, formData?.fwdBankFees]);

    // --- API Calls ---

    const populateCheckData = async () => {
        setLoading(true);
        setErrorMsg(null);
        try {
            const inputDto = {
                LiTg: invoice?.liTg || 0,
                ClientNo: leaseMaster.clNo || 0,
                ProjectNo: leaseMaster.prNo || 0,
                LeaseNo: leaseMaster.lsNo,
            };

            const checkDataPromise = post('/v1.0/leases/master/populate-check', inputDto);
            const companiesPromise = get('/v1.0/leases/master/companies');

            const [checkData, companiesData] = await Promise.all([checkDataPromise, companiesPromise]);

            if (!isMounted.current) {
                return;
            }

            if (response.ok && checkData) {
                let targetCompId = checkData.compId || 0;

                if (Array.isArray(companiesData)) {
                    setCompanies(companiesData);
                    // If backend returned 0 for company, default to the first in the list
                    if (targetCompId === 0 && companiesData.length > 0) {
                        targetCompId = companiesData[0].id;
                    }
                }

                setFormData({
                    liTg: invoice?.liTg || 0,
                    invoiceAdd: invoice?.invoiceAdd || '',
                    leaseNo: leaseMaster.lsNo,
                    invoiceNo: checkData.invoiceNo || 0,
                    invoiceTotal: checkData.invoiceTotal || 0,
                    remainTotal: checkData.remainingTotal || 0,

                    bonus: checkData.bonus || 0,
                    rental: checkData.rental || 0,
                    other: checkData.other || 0,
                    bankFees: checkData.bank || 0,
                    fwdBankFees: checkData.fwdBank || 0,
                    total: checkData.total || 0,

                    checkNo: '',
                    checkDate: checkData.checkDate ? checkData.checkDate.split('T')[0] : new Date().toISOString().split('T')[0],
                    compId: targetCompId,

                    payee: checkData.payee || '',
                    addr1: checkData.addr1 || '',
                    addr2: checkData.addr2 || '',
                    cstz: checkData.cstz || '',
                    memo: '',
                });
            } else {
                setErrorMsg('Failed to load check details.');
            }
        } catch (error) {
            if (isMounted.current) {
                setErrorMsg('Error connecting to server.');
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const handleSave = async () => {
        if (!formData) {
            return;
        }

        if (validationError) {
            alert(`Cannot save: ${validationError}`);
            return;
        }

        if (!isCheckAvailable) {
            alert('Please enter a valid, available Check Number.');
            return;
        }

        const safeCheckNo = parseInt(String(formData.checkNo), 10);
        if (isNaN(safeCheckNo) || safeCheckNo <= 0) {
            alert('Please enter a valid Check Number.');
            return;
        }

        if (!formData.checkDate) {
            alert('Please select a Check Date.');
            return;
        }

        setLoading(true);
        try {
            const payload: IProcessCheckInput = {
                checkNo: safeCheckNo,
                compID: parseInt(String(formData.compId), 10) || 0,
                leaseNo: parseInt(String(formData.leaseNo), 10) || 0,
                invoiceNo1: parseInt(String(formData.invoiceNo), 10) || 0,
                liTg: parseInt(String(formData.liTg), 10) || 0,
                bonus: parseFloat(String(formData.bonus)) || 0,
                rental: parseFloat(String(formData.rental)) || 0,
                bank: parseFloat(String(formData.bankFees)) || 0,
                fwdBank: parseFloat(String(formData.fwdBankFees)) || 0,
                other: parseFloat(String(formData.other)) || 0,
                total: parseFloat(String(formData.total)) || 0,
                remTotal: parseFloat(String(formData.remainTotal)) || 0,
                checkDate: formData.checkDate,
                payee: formData.payee || '',
                addr1: formData.addr1 || '',
                addr2: formData.addr2 || '',
                cstz: formData.cstz || '',
                memoFor: formData.memo || '',
                invoiceAdd: formData.invoiceAdd || '',
            };

            await post('/v1.0/leases/master/process-check', payload);

            if (!isMounted.current) {
                return;
            }

            if (response.ok) {
                onSuccess?.();
                const checkDetails = await get(`/v1.0/leases/master/checks/${safeCheckNo}?compId=${payload.compID}`);

                if (response.ok && checkDetails && checkDetails.length > 0) {
                    setCreatedCheck(checkDetails[0]);
                } else {
                    onClose();
                }
            } else {
                setErrorMsg(getLeaseRecordsApiErrorMessage(response.data, response.status));
            }
        } catch (error) {
            if (isMounted.current) {
                setErrorMsg(getLeaseRecordsApiErrorMessage(undefined, 500));
            }
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const handleChange = (field: keyof ICheckDetails, value: string | number) => {
        if (!formData) {
            return;
        }
        setFormData({ ...formData, [field]: value });
    };

    // --- RENDER LABEL HELPER ---
    const renderCheckNoLabel = () => {
        return (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <span>Check No.</span>
                <span style={{ display: 'flex', alignItems: 'center', marginLeft: '8px', fontSize: '0.75rem', fontWeight: 600 }}>
                    {isValidating && (
                        <>
                            <Loader2 style={styles.spinner} />
                            <span style={{ color: '#6B7280' }}>Checking...</span>
                        </>
                    )}
                    {!isValidating && validationError && <span style={styles.errorText}>{validationError}</span>}
                    {!isValidating && isCheckAvailable && <span style={styles.successText}>Available</span>}
                </span>
            </span>
        );
    };

    const isCreateDisabled = loading || !formData || !formData.compId || !isCheckAvailable || !!validationError;

    if (!isOpen) {
        return null;
    }

    if (createdCheck) {
        return <CheckDetailsModal check={createdCheck} onClose={onClose} />;
    }

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={styles.modalHeader}>
                    <h2 style={styles.modalTitle}>
                        <FileText style={styles.headerIcon} />
                        Create Lessor Direct Check
                    </h2>
                    <button onClick={onClose} style={styles.closeButton} aria-label="Close dialog">
                        <X style={styles.closeIcon} />
                    </button>
                </div>

                {/* Body */}
                <div style={styles.modalBody}>
                    {loading && !formData ? (
                        <div style={styles.loadingState}>
                            <Loader2 style={styles.loaderIcon} />
                            <span style={styles.loadingText}>Loading check details...</span>
                        </div>
                    ) : errorMsg ? (
                        <div style={styles.errorState} role="alert">
                            <AlertTriangle style={styles.errorIcon} />
                            Error: {errorMsg}
                        </div>
                    ) : formData ? (
                        <>
                            <div style={styles.gridContainer}>
                                <div style={styles.col}>
                                    <Field label="Lease No." value={formData.leaseNo.toString()} editable={false} />

                                    {/* Cast to unknown then string to satisfy no-any if Field is strict string */}
                                    <Field
                                        label={renderCheckNoLabel() as unknown as string}
                                        value={formData.checkNo}
                                        onChange={(val) => handleChange('checkNo', val)}
                                        editable={true}
                                    />

                                    <Field
                                        label="Bonus"
                                        type="number"
                                        prefix="$"
                                        value={formData.bonus.toString()}
                                        onChange={(val) => handleChange('bonus', parseFloat(val) || 0)}
                                        editable={true}
                                    />
                                    <Field
                                        label="Rentals"
                                        type="number"
                                        prefix="$"
                                        value={formData.rental.toString()}
                                        onChange={(val) => handleChange('rental', parseFloat(val) || 0)}
                                        editable={true}
                                    />
                                </div>

                                <div style={styles.col}>
                                    <Field label="Invoice No." value={formData.invoiceNo.toString()} editable={false} />
                                    <DateField
                                        label="Check Date"
                                        value={formData.checkDate}
                                        onChange={(val) => handleChange('checkDate', val)}
                                        editable={true}
                                    />
                                    <Field
                                        label="Other"
                                        type="number"
                                        prefix="$"
                                        value={formData.other.toString()}
                                        onChange={(val) => handleChange('other', parseFloat(val) || 0)}
                                        editable={true}
                                    />
                                    <Field
                                        label="Bank Fees"
                                        type="number"
                                        prefix="$"
                                        value={formData.bankFees.toString()}
                                        onChange={(val) => handleChange('bankFees', parseFloat(val) || 0)}
                                        editable={true}
                                    />
                                </div>

                                <div style={styles.col}>
                                    <Field label="Invoice Total" value={formData.invoiceTotal.toFixed(2)} prefix="$" editable={false} />
                                    <Field label="Remain Total" value={formData.remainTotal.toFixed(2)} prefix="$" editable={false} />
                                    <Field label="Total" value={formData.total.toFixed(2)} prefix="$" editable={false} />
                                    <Field
                                        label="Fwd. Bank Fees"
                                        type="number"
                                        prefix="$"
                                        value={formData.fwdBankFees.toString()}
                                        onChange={(val) => handleChange('fwdBankFees', parseFloat(val) || 0)}
                                        editable={true}
                                    />
                                </div>
                            </div>

                            <div style={styles.divider} />

                            <div style={styles.fullWidthContainer}>
                                <div style={styles.row}>
                                    <SelectField
                                        label="Company ID"
                                        value={formData.compId.toString()}
                                        onChange={(val) => handleChange('compId', parseInt(val, 10) || 0)}
                                        editable={true}
                                        options={[
                                            { value: '', label: 'Select...' },
                                            ...companies.map((c) => ({ value: c.id.toString(), label: c.name })),
                                        ]}
                                    />
                                </div>
                                <div style={styles.row}>
                                    <Field label="Payee" value={formData.payee} onChange={(val) => handleChange('payee', val)} editable={true} />
                                </div>
                                <div style={styles.row}>
                                    <Field label="Address" value={formData.addr1} onChange={(val) => handleChange('addr1', val)} editable={true} />
                                </div>
                                <div style={styles.row}>
                                    <Field label="" value={formData.addr2} onChange={(val) => handleChange('addr2', val)} editable={true} />
                                </div>
                                <div style={styles.row}>
                                    <Field
                                        label="City, ST, Zip"
                                        value={formData.cstz}
                                        onChange={(val) => handleChange('cstz', val)}
                                        editable={true}
                                    />
                                </div>
                                <div style={styles.row}>
                                    <Field label="Memo" value={formData.memo} onChange={(val) => handleChange('memo', val)} editable={true} />
                                </div>
                            </div>
                        </>
                    ) : null}
                </div>

                <div style={styles.modalFooter}>
                    <button onClick={onClose} style={styles.buttonSecondary} disabled={loading}>
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        style={{
                            ...styles.buttonPrimary,
                            opacity: isCreateDisabled ? 0.5 : 1,
                            cursor: isCreateDisabled ? 'not-allowed' : 'pointer',
                        }}
                        disabled={isCreateDisabled}
                    >
                        {loading ? 'Processing...' : 'Create'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- Styles ---
const styles: { [key: string]: React.CSSProperties } = {
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 50,
        padding: '1rem',
        backdropFilter: 'blur(1px)',
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        borderRadius: '1rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        width: '100%',
        maxWidth: '850px',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'inherit',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1.5rem',
        borderBottom: '1px solid #E5E7EB',
    },
    modalTitle: { fontSize: '1.25rem', fontWeight: 700, color: '#111827', display: 'flex', alignItems: 'center', margin: 0 },
    headerIcon: { width: '1.5rem', height: '1.5rem', marginRight: '0.5rem', color: '#4F46E5' },
    closeButton: {
        color: '#9CA3AF',
        padding: '0.25rem',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.15s ease-in-out',
    },
    closeIcon: { width: '1.5rem', height: '1.5rem' },
    modalBody: { padding: '1.5rem', display: 'flex', flexDirection: 'column', flexGrow: 1, overflowY: 'auto' },
    gridContainer: { display: 'flex', gap: '1.5rem', marginBottom: '1.5rem' },
    col: { flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem' },
    fullWidthContainer: { display: 'flex', flexDirection: 'column', gap: '1rem' },
    row: { display: 'flex', flexDirection: 'column' },
    divider: { height: '1px', backgroundColor: '#E5E7EB', width: '100%', margin: '1.5rem 0' },
    modalFooter: { padding: '1rem', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' },
    buttonPrimary: {
        padding: '0.6rem 1.5rem',
        border: 'none',
        borderRadius: '0.5rem',
        backgroundColor: '#4F46E5',
        color: 'white',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        transition: 'all 0.15s ease-in-out',
    },
    buttonSecondary: {
        padding: '0.6rem 1.5rem',
        border: '1px solid #D1D5DB',
        borderRadius: '0.5rem',
        backgroundColor: '#FFFFFF',
        color: '#374151',
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.875rem',
        boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
        transition: 'all 0.15s ease-in-out',
    },
    loadingState: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '12.5rem', color: '#4F46E5' },
    loaderIcon: { width: '2rem', height: '2rem', marginRight: '0.5rem', animation: 'spin 1s linear infinite' },
    loadingText: { fontSize: '1.125rem', fontWeight: 500 },
    errorState: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: '8rem',
        color: '#EF4444',
        fontWeight: 500,
        backgroundColor: '#FEF2F2',
        borderRadius: '0.5rem',
        padding: '1rem',
    },
    errorIcon: { width: '1.5rem', height: '1.5rem', marginRight: '0.5rem' },
    spinner: { width: '0.9rem', height: '0.9rem', animation: 'spin 1s linear infinite', color: '#6B7280', marginRight: '4px' },
    errorText: { color: '#EF4444' },
    successText: { color: '#10B981' },
};

export default CreateLessorDirectCheckModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/checks/CreateLessorDirectCheckModal.tsx